import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2, Upload, User, Mail, Phone, FileText, Send } from 'lucide-react';
import { getApiBaseUrl } from '../../lib/apiBase';

const API_BASE_URL = getApiBaseUrl();

const ApplyModal = ({ job, onClose }) => {
  const [status, setStatus] = useState('idle');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', regionCode: '+1', phone: '', linkedin: '', experience: '', details: '' });
  const [errors, setErrors] = useState({});
  const [resume, setResume] = useState(null);

  const isGeneral = job && job.id === 'general';

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) newErrors.phone = 'Phone number must be exactly 10 digits';
    
    if (formData.linkedin && !formData.linkedin.includes('linkedin.com/')) {
        newErrors.linkedin = 'Please enter a valid LinkedIn URL';
    }

    if (!resume) newErrors.resume = 'Please upload your resume (PDF)';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setStatus('submitting');

    const data = new FormData();
    data.append('jobId', isGeneral ? '0' : job.id.toString());
    data.append('jobTitle', isGeneral ? 'General Application' : job.title);
    data.append('name', `${formData.firstName} ${formData.lastName}`.trim());
    data.append('email', formData.email);
    data.append('phone', `${formData.regionCode} ${formData.phone}`);
    data.append('linkedin', formData.linkedin);
    data.append('experience', formData.experience);
    data.append('details', formData.details);
    if (resume) data.append('resume', resume);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const res = await fetch(`${API_BASE_URL}/api/jobs/apply`, {
        method: 'POST',
        body: data,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        setStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', regionCode: '+1', phone: '', linkedin: '', experience: '', details: '' });
        setResume(null);
        setErrors({});
      } else {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Server rejected the application');
      }
    } catch (error) {
      const isTimeout = error?.name === 'AbortError';
      if (!isTimeout) console.error('Submission error:', error);
      setStatus('idle');
      const message = isTimeout
        ? 'Server took too long to respond'
        : (error?.message || 'Failed to submit application');
      setErrors({ ...errors, submit: `Submission failed: ${message}. Please try again or contact support.` });
    }
  };

  if (!job) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="relative w-full max-w-2xl bg-white border border-slate-200 rounded-2xl p-8 md:p-12 overflow-y-auto max-h-[90vh] shadow-2xl"
      >
        <button 
          onClick={onClose} 
          aria-label="Close application form"
          className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:text-red-500 hover:bg-red-50 transition-all duration-200 z-50"
        >
          <X className="w-5 h-5" />
        </button>

        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="text-center py-20 space-y-8"
            >
              <div className="relative inline-block px-4">
                <div className="w-24 h-24 bg-green-50 text-green-500 rounded-3xl rotate-12 flex items-center justify-center mx-auto shadow-sm">
                  <div className="-rotate-12">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-3xl font-black text-primary tracking-tight">Application Received</h3>
                <p className="text-slate-500 text-[15px] max-w-sm mx-auto font-medium leading-relaxed">
                  {isGeneral 
                    ? "Thank you for joining our talent pool! Our recruiters will reach out as soon as a matching opportunity arises."
                    : <>Your profile for <span className="text-secondary font-black underline decoration-blue-500/20 underline-offset-4">{job.title}</span> has been successfully submitted.</>
                  }
                </p>
              </div>

              <button
                onClick={onClose}
                className="px-12 py-4 bg-primary text-white font-black rounded-2xl hover:bg-secondary transition-all shadow-xl shadow-blue-500/20 hover:shadow-blue-500/30 active:scale-95"
              >
                Done
              </button>
            </motion.div>
          ) : (
            <motion.div key="form" exit={{ opacity: 0, y: -20 }}>
              <div className="mb-10 space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-[2px] bg-secondary rounded-full" />
                    <span className="text-secondary font-black text-[11px] uppercase tracking-[0.3em]">Career Opportunity</span>
                  </div>
                  <h2 className="text-4xl font-black text-primary tracking-tight leading-[1.1]">
                    {isGeneral ? "Join Our Talent Network" : `Apply for ${job.title}`}
                  </h2>
                </div>
                
                <div className={`${isGeneral ? 'bg-primary' : 'bg-slate-50'} p-8 rounded-3xl border ${isGeneral ? 'border-primary shadow-2xl shadow-blue-900/10' : 'border-slate-100'} relative overflow-hidden group`}>
                  {isGeneral && <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl" />}
                  <h4 className={`text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${isGeneral ? 'text-blue-300' : 'text-primary'}`}>
                    {isGeneral ? 'Strategic Pool Overview' : 'Position Overview'}
                  </h4>
                  <p className={`text-sm leading-relaxed font-medium ${isGeneral ? 'text-blue-50' : 'text-slate-600'}`}>
                    {job.description}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.submit && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                    {errors.submit}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">First Name *</label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                      <input 
                        required 
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-4 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium placeholder:font-normal placeholder:text-slate-300" 
                        placeholder="Alex" 
                        value={formData.firstName} 
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Last Name *</label>
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                      <input 
                        required 
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-4 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium placeholder:font-normal placeholder:text-slate-300" 
                        placeholder="Rivera" 
                        value={formData.lastName} 
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Email Address *</label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                      <input 
                        required 
                        type="email" 
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-4 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium placeholder:font-normal placeholder:text-slate-300" 
                        placeholder="alex@example.com" 
                        value={formData.email} 
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Phone Number *</label>
                    <div className="flex gap-3">
                      <select 
                        className="w-28 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-4 text-slate-800 focus:bg-white focus:border-primary outline-none transition-all text-sm font-bold appearance-none cursor-pointer"
                        value={formData.regionCode}
                        onChange={(e) => setFormData({ ...formData, regionCode: e.target.value })}
                      >
                        <option value="+1">+1 US</option>
                        <option value="+91">+91 IN</option>
                        <option value="+44">+44 UK</option>
                        <option value="+61">+61 AU</option>
                        <option value="+49">+49 DE</option>
                        <option value="+33">+33 FR</option>
                      </select>
                      <div className="relative flex-1 group">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                        <input 
                          required 
                          type="tel" 
                          className={`w-full bg-slate-50 border ${errors.phone ? 'border-red-300' : 'border-slate-200'} rounded-2xl pl-12 pr-4 py-4 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium placeholder:font-normal placeholder:text-slate-300`} 
                          placeholder="Phone number"
                          value={formData.phone} 
                          onChange={(e) => {
                            setFormData({ ...formData, phone: e.target.value.replace(/[^\d- ]/g, '') });
                            if (errors.phone) setErrors({ ...errors, phone: null });
                          }} 
                        />
                      </div>
                    </div>
                    {errors.phone && <p className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-wider">{errors.phone}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">LinkedIn Profile</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors font-bold text-xs flex items-center justify-center">in</div>
                    <input 
                      className={`w-full bg-slate-50 border ${errors.linkedin ? 'border-red-300' : 'border-slate-200'} rounded-2xl pl-12 pr-4 py-4 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium placeholder:font-normal placeholder:text-slate-300`} 
                      placeholder="linkedin.com/in/username" 
                      value={formData.linkedin} 
                      onChange={(e) => {
                        setFormData({ ...formData, linkedin: e.target.value });
                        if (errors.linkedin) setErrors({ ...errors, linkedin: null });
                      }} 
                    />
                  </div>
                  {errors.linkedin && <p className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-wider">{errors.linkedin}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Resume / CV (PDF) *</label>
                  <div className={`relative group border-2 border-dashed ${errors.resume ? 'border-red-300 bg-red-50/10' : 'border-slate-200 bg-slate-50/50'} hover:border-primary/50 transition-all rounded-2xl p-10 text-center hover:bg-blue-50/30 active:scale-[0.99]`}>
                    <input
                      type="file"
                      accept=".pdf"
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                      onChange={(e) => {
                          const file = e.target.files ? e.target.files[0] : null;
                          if (file && file.size > 5 * 1024 * 1024) {
                            setErrors({ ...errors, resume: 'File is too large. Maximum size is 5MB.' });
                            setResume(null);
                          } else {
                            setResume(file);
                            if (errors.resume) setErrors({ ...errors, resume: null });
                          }
                      }}
                    />
                    {resume ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-green-50 text-green-500 rounded-2xl flex items-center justify-center shadow-sm">
                          <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <span className="text-base font-black text-primary">{resume.name}</span>
                        <span className="text-[10px] text-slate-400 uppercase font-black tracking-widest">File securely attached</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 bg-white text-blue-500 rounded-2xl flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                          <Upload className="w-8 h-8" />
                        </div>
                        <div>
                          <p className="text-base font-black text-primary mb-1">Upload your resume</p>
                          <p className="text-[11px] text-slate-500 uppercase font-bold tracking-widest">PDF format preferred (Max 5MB)</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.resume && <p className="text-[10px] text-red-500 font-bold ml-1 uppercase tracking-wider">{errors.resume}</p>}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Tell us your story</label>
                  <div className="relative group">
                    <FileText className="absolute left-4 top-5 w-4 h-4 text-slate-400 group-focus-within:text-primary transition-colors" />
                    <textarea 
                      rows={4} 
                      className="w-full bg-slate-50 border border-slate-200 rounded-2xl pl-12 pr-4 py-5 text-slate-800 focus:bg-white focus:border-primary focus:ring-4 focus:ring-blue-500/5 outline-none transition-all resize-none font-medium text-[15px] placeholder:font-normal placeholder:text-slate-400" 
                      placeholder="What makes you a great fit for Libsys?" 
                      value={formData.details} 
                      onChange={(e) => setFormData({ ...formData, details: e.target.value })} 
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    type="submit" 
                    disabled={status === 'submitting'} 
                    className="w-full py-5 bg-primary hover:bg-secondary text-white font-black rounded-2xl transition-all flex items-center justify-center gap-3 shadow-2xl shadow-blue-500/20 active:scale-95 disabled:opacity-70"
                  >
                    {status === 'submitting' ? (
                      <><Loader2 className="w-6 h-6 animate-spin" /> Processing...</>
                    ) : (
                      <><Send className="w-5 h-5" /> {isGeneral ? 'Submit to Talent Pool' : 'Submit Application'}</>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-slate-400 mt-6 font-medium">
                    By submitting, you agree to our <a href="/privacy" className="text-primary hover:underline font-bold">Privacy Policy</a> and <a href="/terms" className="text-primary hover:underline font-bold">Terms of Service</a>.
                  </p>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default ApplyModal;
